import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTier } from '../../../../contexts/TierContext';
import { PointsInfo, UserTier } from '../../mockData';
import { TierProgressModal } from '../TierProgressModal/TierProgressModal';
import carretIcon from '../../../../assets/icons/circled-right-carret.svg';
import './PointsOverview.scss';

interface PointsOverviewProps {
  projectValue: PointsInfo;
  engagement: PointsInfo;
}

const CircularProgress: React.FC<{
  current: number;
  total: number;
  label: string;
  id: string;
  isAccolade?: boolean;
}> = ({ current, total, label, id, isAccolade }) => {
  const percentage = isAccolade ? 100 : (current / total) * 100;
  const radius = 85;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="circular-progress">
      <h3 className="circular-progress__label">{label}</h3>
      <div className="circular-progress__container">
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <defs>
            <linearGradient
              id={`progress-gradient-${id}`}
              gradientUnits="userSpaceOnUse"
              gradientTransform="rotate(90, 100, 100)"
            >
              <stop offset="0%" style={{ stopColor: `var(--gradient-start)` }} />
              <stop offset="17%" style={{ stopColor: `var(--gradient-color-2)` }} />
              <stop offset="39%" style={{ stopColor: `var(--gradient-color-3)` }} />
              <stop offset="64.2%" style={{ stopColor: `var(--gradient-color-4)` }} />
              <stop offset="85%" style={{ stopColor: `var(--gradient-color-5)` }} />
              <stop offset="100%" style={{ stopColor: `var(--gradient-end)` }} />
            </linearGradient>
          </defs>
          {/* Background circle */}
          {/* Background track */}
          <circle
            className="circular-progress__track"
            cx="100"
            cy="100"
            r={radius}
            fill="none"
          />
          {/* Inner circle */}
          <circle
            className="circular-progress__inner"
            cx="100"
            cy="100"
            r={radius}
            fill="none"
          />
          {/* Progress circle */}
          <motion.circle
            className="circular-progress__progress"
            cx="100"
            cy="100"
            r={radius}
            stroke={`url(#progress-gradient-${id})`}
            strokeWidth="12"
            fill="none"
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: "easeInOut" }}
            style={{
              strokeDasharray: circumference
            }}
          />
        </svg>
        <div className="circular-progress__content">
          <div className="circular-progress__points">
            <span className="current">{current.toLocaleString()}/</span>
            {id === 'project' && <br></br>}
            <span className="total">
              {isAccolade
                ? (id === 'project' ? '500,000+' : '50')
                : total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PointsOverview: React.FC<PointsOverviewProps> = ({
  projectValue,
  engagement,
}) => {
  const { t } = useTranslation('home');
  const { tier } = useTier();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Function to get the next tier based on current tier
  const getNextTier = (currentTier: UserTier): UserTier => {
    const tierProgression: UserTier[] = ['bronze', 'silver', 'gold', 'platinum', 'accolade'];
    const currentIndex = tierProgression.indexOf(currentTier);

    // If current tier is the highest or not found, return current tier
    if (currentIndex === -1 || currentIndex === tierProgression.length - 1) {
      return currentTier;
    }

    // Return the next tier in the progression
    return tierProgression[currentIndex + 1];
  };

  // Get the tier to display (current tier for accolade, next tier for others)
  const displayTier = tier === 'accolade' ? tier : getNextTier(tier);
  return (
    <div className={`points-overview points-overview--${tier}`}>
      <div className="points-overview__container">
        <CircularProgress
          current={projectValue.current}
          total={projectValue.total}
          label={t('points.specificationPoints')}
          id="project"
          isAccolade={tier === 'accolade'}
        />
        <div className="points-overview__progress-text">
          {tier === 'accolade'
            ? t('points.yourTier')
            : t('points.progressTo')}
          <br></br>
          <div className="points-overview__tier-container">
            <span className="points-overview__progress-Tier">
              {t(`dashboard.tiers.${displayTier}`)}
            </span>
            <button
              className="points-overview__tier-info-button"
              onClick={() => setIsModalOpen(true)}
              aria-label="View tier information"
            >
              <img src={carretIcon} alt="" />
            </button>
          </div>
        </div>
        <CircularProgress
          current={engagement.current}
          total={engagement.total}
          label={t('points.engagement')}
          id="engagement"
          isAccolade={tier === 'accolade'}
        />
      </div>
      <TierProgressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userPoints={{
          projectValue: {
            current: projectValue.current,
            total: projectValue.total
          },
          engagement: {
            current: engagement.current,
            total: engagement.total
          }
        }}
      />
    </div>
  );
};
